import CubeIndex, {AxisItem} from '../cube/CubeIndex';
import {computeDelta, previousValue, respondents, roundValue} from '../render/format';
import {isLowBase} from '../render/lowBase';
import {mountTable} from '../table/TableView';
import {TableCell, TableColumn, TableRow, TableSpec} from '../table/model';
import {Cube, CubeMeasure, MarkRenderer, RenderContext, RenderSpec} from '../types';

export type RankingColumn = 'rank' | 'label' | 'value' | 'previous' | 'delta' | 'rankChange' | 'n' | 'measures';

export interface RankingOptions {
    by: string | null;
    better: 'higher' | 'lower';
    ties: 'competition' | 'dense';
    top: number | null;
    bottom: number | null;
    highlight: {top: number | null; bottom: number | null};
    /** columns: one label column per rows dimension; joined: one with the labels joined */
    labels: 'columns' | 'joined';
    /** Shown columns in order; label stands for the label column(s), measures for the other measures */
    columns: RankingColumn[];
    bar: boolean;
    total: boolean;
    lowBase: 'exclude' | 'mark';
}

const DEFAULTS: RankingOptions = {
    by: null,
    better: 'higher',
    ties: 'competition',
    top: null,
    bottom: null,
    highlight: {top: null, bottom: null},
    labels: 'columns',
    columns: ['rank', 'label', 'value', 'n', 'measures'],
    bar: false,
    total: false,
    lowBase: 'exclude',
};

/** The server fills the defaults; this only guards a spec that skipped the loader (tests, older caches). */
export function rankingOptions(spec: RenderSpec): RankingOptions {
    const options = spec.options ?? {};

    return {
        ...DEFAULTS,
        ...options,
        highlight: {...DEFAULTS.highlight, ...options.highlight},
        columns: Array.isArray(options.columns) ? options.columns : DEFAULTS.columns,
    };
}

const COLUMN = {label: 'label', rank: 'rank', value: 'value', previous: 'previous', delta: 'delta', rankChange: 'rankChange', count: 'n'};

interface Entry {
    row: TableRow;
    /** Rounded value of the `by` measure; null = not ranked */
    value: number | null;
    previous: number | null;
}

function rowsChannel(spec: RenderSpec): string[] {
    const rows = spec.encoding.rows;

    return Array.isArray(rows) ? rows : rows ? [rows] : [];
}

function combinations(index: CubeIndex, channels: string[]): AxisItem[][] {
    return channels.reduce<AxisItem[][]>(
        (result, id) => result.flatMap(combination => index.items(id).map(item => [...combination, item])),
        [[]],
    );
}

function outranks(better: RankingOptions['better']): (a: number, b: number) => boolean {
    return better === 'higher' ? (a, b) => a > b : (a, b) => a < b;
}

/** Rank per value among `values`; equal values share a rank, the next one skips (competition) or not (dense). */
function ranker(values: number[], options: RankingOptions, reverse = false): (value: number) => number {
    const beats = outranks(options.better);
    const beaten = reverse ? (a: number, b: number) => beats(b, a) : beats;
    const pool = options.ties === 'dense' ? values.filter((value, position) => values.indexOf(value) === position) : values;

    return value => 1 + pool.filter(other => beaten(other, value)).length;
}

/**
 * Every member (combination) of the rows ranked by one measure, in rank order: the benchmark total first, then the
 * ranked rows, then those left out of the ranking (no value, missing member, low base when excluded) in cube order.
 * Ties compare the rounded values, i.e. what the user sees, and keep the cube order (`style.sort`).
 */
export function rankingTable(cube: Cube, spec: RenderSpec, ctx: RenderContext): TableSpec {
    const options = rankingOptions(spec);
    const index = new CubeIndex(cube);
    const channels = rowsChannel(spec).filter(id => index.dimensionIndex(id) !== -1);
    // Columns use only a measure's precision and unit; its colour is not drawn
    const measures = cube.measures.map(({color, ...measure}): CubeMeasure => measure);
    const by = measures.find(measure => measure.id === options.by) ?? measures[0];
    const others = measures.filter(measure => measure !== by);
    const labels = ctx.labels ?? {};

    const joined = options.labels === 'joined';
    const dimensionLabel = (id: string) => cube.dimensions[index.dimensionIndex(id)].label;
    const labelIds = joined ? [COLUMN.label] : channels.map(id => `${COLUMN.label}.${id}`);
    const available: Record<RankingColumn, TableColumn[]> = {
        rank: [{id: COLUMN.rank, kind: 'rank', label: labels.rank ?? 'rank'}],
        label: joined
            ? [{id: COLUMN.label, kind: 'label', label: channels.map(dimensionLabel).join(' / ')}]
            : channels.map((id, position) => ({id: labelIds[position], kind: 'label', label: dimensionLabel(id)})),
        value: by ? [{
            id: COLUMN.value,
            kind: options.bar ? 'bar' : 'value',
            label: by.label,
            measure: by,
            ...(options.bar && by.scale ? {range: [by.scale.min, by.scale.max] as [number, number]} : {}),
        }] : [],
        previous: by ? [{id: COLUMN.previous, kind: 'value', label: cube.meta.previousTerm?.label ?? labels.previous ?? 'previous', measure: by}] : [],
        delta: by ? [{id: COLUMN.delta, kind: 'delta', label: 'Δ', measure: by, good: options.better === 'higher' ? 1 : -1, against: 'previous'}] : [],
        rankChange: [{id: COLUMN.rankChange, kind: 'delta', label: labels.rankChange ?? 'rankChange', good: 1}],
        n: [{id: COLUMN.count, kind: 'count', label: 'n'}],
        measures: others.map((measure): TableColumn => ({id: `m.${measure.id}`, kind: 'value', label: measure.label, measure})),
    };
    const columns = options.columns.flatMap(key => available[key] ?? []);
    // The rank and labels in front stay in view while the rest scrolls horizontally
    for (const column of columns) {
        if (column.kind !== 'rank' && column.kind !== 'label') {
            break;
        }
        column.sticky = true;
    }

    if (!by || channels.length === 0) {
        return {columns, rows: []};
    }

    let total: TableRow | undefined;
    const entries: Entry[] = [];
    combinations(index, channels).forEach(combination => {
        const totals = combination.filter(item => item.member?.role === 'total').length;
        const positions = Object.fromEntries(channels.map((id, position) => [id, combination[position].index]));
        const value = (measure: CubeMeasure) => index.value(positions, measure.id);
        if (totals > 0 && (totals < channels.length || !options.total)) {
            return; // partial totals are rollups, not members
        }
        if (!measures.some(measure => (value(measure)?.count ?? 0) > 0)) {
            return;
        }

        const cell = value(by);
        const lowBase = isLowBase(cube, cell);
        const excludedLowBase = options.lowBase === 'exclude' && cell?.lowBase === true;
        const isTotal = totals > 0;
        // The total row names itself once; its members are the same total on every dimension
        const labelValues = isTotal
            ? [combination[0].label, ...labelIds.slice(1).map(() => '')]
            : joined ? [combination.map(item => item.label).join(' / ')] : combination.map(item => item.label);
        const labelCells: TableCell[] = labelValues.map(value => ({value}));
        const color = combination[0].member?.color;
        if (color && labelCells.length > 0) {
            labelCells[0].color = color;
        }
        const previous = previousValue(cell, by);
        const cells: Record<string, TableCell> = {
            ...Object.fromEntries(labelIds.map((id, position) => [id, labelCells[position]])),
            [COLUMN.value]: {value: cell?.value ?? null, lowBase: lowBase || excludedLowBase},
            [COLUMN.previous]: {value: previous},
            [COLUMN.delta]: {value: previous === null ? null : computeDelta(cell?.value, previous, by, ctx)?.value ?? null},
            [COLUMN.count]: {value: respondents(cell), lowBase: lowBase || excludedLowBase},
        };
        others.forEach(measure => {
            const other = value(measure);
            cells[`m.${measure.id}`] = {value: other?.value ?? null, lowBase: isLowBase(cube, other)};
        });

        const row: TableRow = {key: JSON.stringify(combination.map(item => item.id)), role: isTotal ? 'total' : 'data', cells};
        if (row.role === 'total') {
            total = row;
            return;
        }

        const missing = combination.some(item => item.member?.role === 'missing');
        const rounded = roundValue(cell?.value, by);
        const ranked = !missing && !excludedLowBase && rounded !== null;
        if (!ranked) {
            row.unranked = true;
            if (excludedLowBase) {
                cells[COLUMN.rank] = {value: null, title: labels.lowBaseExcluded ?? 'lowBaseExcluded'};
            }
        }
        entries.push({row, value: ranked ? rounded : null, previous: missing ? null : previous});
    });

    const ranked = entries.filter(entry => entry.value !== null);
    const values = ranked.map(entry => entry.value!);
    const rank = ranker(values, options);
    const reverseRank = ranker(values, options, true);
    const previousValues = ranked.map(entry => entry.previous).filter((value): value is number => value !== null);
    const previousRank = ranker(previousValues, options);

    ranked.forEach(entry => {
        const position = rank(entry.value!);
        const {row} = entry;
        row.cells[COLUMN.rank] = {value: position};
        if (entry.previous !== null) {
            const before = previousRank(entry.previous);
            row.cells[COLUMN.rankChange] = {value: before - position, title: `${labels.previous ?? 'previous'}: ${before}.`};
        }
        if (options.highlight.top !== null && position <= options.highlight.top) {
            row.highlight = 'top';
        } else if (options.highlight.bottom !== null && reverseRank(entry.value!) <= options.highlight.bottom) {
            row.highlight = 'bottom';
        }
    });

    // Array.prototype.sort is stable, so ties stay in cube order
    const ordered = [...ranked].sort((a, b) => (a.row.cells[COLUMN.rank].value as number) - (b.row.cells[COLUMN.rank].value as number));
    const unranked = entries.filter(entry => entry.value === null);

    return {
        columns,
        rows: [...(total ? [total] : []), ...ordered.map(entry => entry.row), ...unranked.map(entry => entry.row)],
        sort: options.columns.includes('rank') ? {column: COLUMN.rank, dir: 'asc'} : undefined,
    };
}

/** What is shown: the `top` best and `bottom` worst ranked rows with a gap row where some were cut; CSV keeps them all. */
export function rankingDisplay(table: TableSpec, spec: RenderSpec): TableSpec {
    const {top, bottom} = rankingOptions(spec);
    if (top === null && bottom === null) {
        return table;
    }
    const totals = table.rows.filter(row => row.role === 'total');
    const ranked = table.rows.filter(row => row.role === 'data' && !row.unranked);
    const unranked = table.rows.filter(row => row.role === 'data' && row.unranked);
    const rank = (row: TableRow) => row.cells[COLUMN.rank].value;
    // A cut never splits a tie: rows sharing the rank of the last one kept are kept too
    let headEnd = Math.min(top ?? 0, ranked.length);
    while (headEnd > 0 && headEnd < ranked.length && rank(ranked[headEnd]) === rank(ranked[headEnd - 1])) {
        headEnd++;
    }
    let tailStart = Math.max(ranked.length - (bottom ?? 0), 0);
    while (tailStart < ranked.length && tailStart > 0 && rank(ranked[tailStart - 1]) === rank(ranked[tailStart])) {
        tailStart--;
    }
    if (headEnd >= tailStart) {
        return table;
    }
    const head = ranked.slice(0, headEnd);
    const tail = ranked.slice(tailStart);

    return {...table, rows: [...totals, ...head, {key: '__gap__', role: 'gap', cells: {}}, ...tail, ...unranked]};
}

const renderRanking: MarkRenderer = (cube, spec, ctx) => {
    const table = rankingTable(cube, spec, ctx);
    const shown = rankingDisplay(table, spec);

    return {type: 'html', render: el => mountTable(el, shown, ctx), table};
};

export default renderRanking;
